import { Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { useAllMarkets } from "@/hooks/useMarkets";
import { useUserBets, useUnclaimedWinnings, useClaimWinnings } from "@/hooks/useBetting";
import { formatUSDT } from "@/lib/contracts";

export default function MyBetsPage() {
  const { address, isConnected } = useAccount();

  if (!isConnected) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Portfolio</h2>
          <p className="text-gray-600">Connect your wallet to view your prediction history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Portfolio</h1>
      <MyBetsList userAddress={address as `0x${string}`} />
    </div>
  );
}

function MyBetsList({ userAddress }: { userAddress: `0x${string}` }) {
  const { markets, isLoading } = useAllMarkets();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse p-4">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (markets.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-600 mb-4">No markets available yet.</p>
        <Link to="/create" className="btn btn-primary">
          Create a Market
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {markets.map((market) => (
        <MarketBetCard
          key={market.address}
          marketAddress={market.address}
          question={market.question}
          isResolved={market.isResolved}
          winningOutcome={market.winningOutcome}
          userAddress={userAddress}
        />
      ))}
    </div>
  );
}

function MarketBetCard({
  marketAddress,
  question,
  isResolved,
  winningOutcome,
  userAddress,
}: {
  marketAddress: `0x${string}`;
  question: string;
  isResolved: boolean;
  winningOutcome: boolean;
  userAddress: `0x${string}`;
}) {
  const { data: bets, isLoading } = useUserBets(marketAddress, userAddress);
  const { data: unclaimedWinnings } = useUnclaimedWinnings(marketAddress, userAddress);
  const { claimWinnings, isPending, isSuccess } = useClaimWinnings();

  // Skip if no bets
  if (isLoading) {
    return null;
  }

  if (!bets || bets.length === 0) {
    return null;
  }

  const totalStaked = bets.reduce((sum, bet) => sum + bet.amount, 0n);
  const hasUnclaimedWinnings = unclaimedWinnings !== undefined && unclaimedWinnings > 0n;

  return (
    <div className="card overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <Link to={`/market/${marketAddress}`} className="hover:text-primary-600">
            <h3 className="font-medium text-gray-900 line-clamp-2">{question}</h3>
          </Link>
          {isResolved ? (
            <span className={`badge ml-2 flex-shrink-0 ${winningOutcome ? "badge-green" : "badge-red"}`}>
              {winningOutcome ? "YES" : "NO"}
            </span>
          ) : (
            <span className="badge badge-yellow ml-2 flex-shrink-0">Active</span>
          )}
        </div>

        {/* Bets List */}
        <div className="space-y-2 mb-3">
          {bets.map((bet, i) => {
            const isWinner = isResolved && bet.outcome === winningOutcome;
            const isLoser = isResolved && bet.outcome !== winningOutcome;

            return (
              <div
                key={i}
                className={`flex justify-between items-center p-2 rounded ${
                  isWinner
                    ? "bg-green-50"
                    : isLoser
                    ? "bg-red-50"
                    : bet.outcome
                    ? "bg-green-50/50"
                    : "bg-red-50/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-medium ${
                      bet.outcome ? "text-green-700" : "text-red-700"
                    }`}
                  >
                    {bet.outcome ? "YES" : "NO"}
                  </span>
                  <span className="text-gray-600">{formatUSDT(bet.amount)} USDT</span>
                </div>
                <div className="flex items-center gap-2">
                  {isWinner && !bet.claimed && (
                    <span className="text-xs text-green-600 font-medium">Won!</span>
                  )}
                  {isLoser && <span className="text-xs text-red-600">Lost</span>}
                  {bet.claimed && <span className="badge badge-gray text-xs">Claimed</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Total */}
        <div className="flex justify-between text-sm text-gray-600 pt-2 border-t border-gray-100">
          <span>Total Staked</span>
          <span className="font-medium">{formatUSDT(totalStaked)} USDT</span>
        </div>
      </div>

      {/* Claim Section */}
      {hasUnclaimedWinnings && (
        <div className="bg-green-50 px-4 py-3 border-t border-green-100">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-green-800 font-medium">Winnings available:</span>
              <span className="text-green-700 font-bold ml-2">
                {formatUSDT(unclaimedWinnings)} USDT
              </span>
            </div>
            <button
              onClick={() => claimWinnings(marketAddress)}
              disabled={isPending}
              className="btn btn-success btn-sm"
            >
              {isPending ? "Claiming..." : isSuccess ? "Claimed!" : "Claim"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
